import React, { useMemo, type ReactNode, type Ref } from 'react';
import { ThemeProvider, useTheme } from 'styled-components';
import { windowSurfaceOf } from './AppWindow.constants';
import { Window, WindowBar } from './AppWindow.styled';

interface AppWindowProps {
  readonly windowRef?: Ref<HTMLDivElement>;
  readonly bar?: ReactNode;
  readonly children: ReactNode;
  readonly dataTestId?: string;
}

/** The box an app opens in, wherever it opens: its bar, then the app. */
const AppWindow: React.FC<AppWindowProps> = ({ windowRef, bar, children, dataTestId = 'app-window' }) => (
  <Window ref={windowRef} data-testid={dataTestId}>
    {bar && <WindowBar>{bar}</WindowBar>}
    {children}
  </Window>
);

/** The app's page paints the theme's surface; inside a window that surface is the window's. */
export const WindowSurface: React.FC<{ readonly children: ReactNode }> = ({ children }) => {
  const base = useTheme();
  const theme = useMemo(
    () => ({ ...base, colors: { ...base.colors, surface: windowSurfaceOf(base) } }),
    [base],
  );
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default AppWindow;
